import React from "react";
import styled from "styled-components";
import { getBotIcon } from "../utils/botFormatting";

// Base container for bot display
const BotDisplayContainer = styled.div`
  display: flex;
  align-items: ${({ $variant }) => ($variant === "card" ? "center" : "center")};
  gap: ${({ $variant }) =>
    $variant === "list" ? "0.5rem" : $variant === "card" ? "0" : "0.5rem"};
  flex-direction: ${({ $variant }) => ($variant === "card" ? "column" : "row")};
  text-align: ${({ $variant }) => ($variant === "card" ? "center" : "left")};
`;

// Bot icon component
const BotIcon = styled.span`
  font-size: ${({ $variant }) =>
    $variant === "card"
      ? "2.5rem"
      : $variant === "list"
      ? "1.5rem"
      : "1.25rem"};
  margin-right: ${({ $variant }) => ($variant === "list" ? "0.5rem" : "0")};
  margin-bottom: ${({ $variant }) => ($variant === "card" ? "1rem" : "0")};

  @media (max-width: 768px) {
    font-size: ${({ $variant }) =>
      $variant === "card"
        ? "2rem"
        : $variant === "list"
        ? "1.5rem"
        : "1.25rem"};
  }
`;

// Bot content wrapper
const BotContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ $variant }) => ($variant === "card" ? "0 2rem" : "0")};
`;

// Bot title component
const BotTitle = styled.span`
  font-weight: bold;
  font-size: ${({ $variant }) => ($variant === "card" ? "1.25rem" : "inherit")};
  display: block;
  ${({ $variant }) => $variant === "card" && "margin-bottom: 1rem;"}
`;

// Bot description component
const BotDescription = styled.div`
  color: ${({ $variant }) => ($variant === "card" ? "#6c757d" : "#666")};
  font-size: ${({ $variant }) => ($variant === "list" ? "1rem" : "inherit")};
  margin-top: ${({ $variant }) =>
    $variant === "list" ? "0.25rem" : $variant === "card" ? "0" : "0"};
  line-height: ${({ $variant }) =>
    $variant === "list" ? "1.25" : $variant === "card" ? "1.4" : "normal"};

  ${({ $variant }) =>
    $variant === "card" &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      font-size: 1rem;
      margin: 0.75rem 0 0.5rem 0;
    }
  `}
`;

/**
 * Flexible bot display component
 * @param {Object} bot - Bot object with name, description, etc.
 * @param {string} variant - Display variant: "list", "card", or "checkbox"
 * @param {boolean} showDescription - Whether to show the description
 * @param {React.ReactNode} children - Additional content to render
 */
function BotDisplay({
  bot,
  variant = "list",
  showDescription = true,
  children,
  ...props
}) {
  if (!bot) return null;

  return (
    <BotDisplayContainer $variant={variant} {...props}>
      <BotIcon $variant={variant}>{getBotIcon(bot)}</BotIcon>
      <BotContent $variant={variant}>
        <BotTitle $variant={variant}>{bot.name}</BotTitle>
        {showDescription && bot.description && (
          <BotDescription $variant={variant}>{bot.description}</BotDescription>
        )}
      </BotContent>
      {children}
    </BotDisplayContainer>
  );
}

// Export individual styled components for cases where more customization is needed
export {
  BotDisplayContainer as BotName,
  BotIcon,
  BotTitle,
  BotDescription,
  BotContent
};

export default BotDisplay;
